import { useParams, Outlet, NavLink } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWork,
  blameQueryOptions,
  diffQueryOptions,
  completeProvisionsQueryOptions,
} from '../api/hooks';
import { QueryState } from '../components/common/QueryState';
import { OriginalPdfLink } from '../components/common/OriginalPdfLink';
import { originalPdfForWork } from '../api/originalPdfs';
import { Badge } from '../components/ui/Badge';
import {
  CalendarDays,
  FileCode2,
  GitBranch,
  GitCompareArrows,
  GitCommitHorizontal,
  History,
  LockKeyhole,
  Sparkles,
} from 'lucide-react';
import { formatLegalTitle } from '../components/ui/formatLegalTitle';
import './work.css';

export function Component() {
  const { workId = '' } = useParams<{ workId: string }>();
  const workQuery = useWork(workId);
  const queryClient = useQueryClient();
  const prefetchView = (path: string) => {
    const timeline = workQuery.data?.timeline;
    if (!timeline?.length) return;
    const latest = timeline[timeline.length - 1]!.version_id;
    if (path === 'blame')
      void queryClient.prefetchQuery(blameQueryOptions(workId, latest));
    if (path === 'provisions')
      void queryClient.prefetchQuery(completeProvisionsQueryOptions(latest));
    if (path === 'diff') {
      const changed = [...timeline]
        .reverse()
        .find((version) => version.changes_applied > 0);
      const to = changed?.version_id || latest;
      const target = timeline.findIndex((version) => version.version_id === to);
      const from =
        timeline[target > 0 ? target - 1 : Math.max(timeline.length - 2, 0)]!.version_id;
      void queryClient.prefetchQuery(diffQueryOptions(from, to));
    }
  };

  const tabs = [
    { label: 'History', detail: 'Versions over time', path: '', icon: History },
    { label: 'Impact', detail: 'Amendment effect', path: 'graph', icon: GitBranch },
    {
      label: 'Read Act',
      detail: 'Consolidated text',
      path: 'provisions',
      icon: FileCode2,
    },
    {
      label: 'Compare',
      detail: 'Version changes',
      path: 'diff',
      icon: GitCompareArrows,
    },
    {
      label: 'Sources',
      detail: 'Source provenance',
      path: 'blame',
      icon: GitCommitHorizontal,
    },
    {
      label: 'Ask SLAD',
      detail: 'Research assistant',
      path: 'ask',
      icon: Sparkles,
    },
  ];

  return (
    <div className="sedlex-work-layout">
      <QueryState query={workQuery}>
        {(data) => (
          <div className="sedlex-work-header">
            <div className="sedlex-repo-path">
              <span>Sri Lankan legislation</span>
              <b>·</b>
              <strong>{data.work.domain || 'Statute'}</strong>
              <Badge variant="neutral">
                <LockKeyhole aria-hidden="true" /> Public research corpus
              </Badge>
            </div>
            <h1 className="sedlex-work-title">
              {formatLegalTitle(data.work.short_title)}
            </h1>
            <div className="sedlex-work-meta">
              <span>
                <GitCommitHorizontal aria-hidden="true" />
                {data.work.version_count} versions
              </span>
              <span>
                <GitBranch aria-hidden="true" />
                {data.work.amendment_count} amendments
              </span>
              <span>
                <CalendarDays aria-hidden="true" />
                Source Act{' '}
                {data.work.first_enacted_date ||
                  data.work.source_year ||
                  'date unavailable'}
              </span>
              {data.work.latest_effective_date && (
                <span>Latest {data.work.latest_effective_date}</span>
              )}
              <Badge variant="accent">{data.work.domain || 'Statute'}</Badge>
              <OriginalPdfLink
                pdf={originalPdfForWork(workId)}
                label="Principal Act PDF"
              />
            </div>
            {data.work.engine_release && (
              <details className="sedlex-engine-note" role="note">
                <summary>Reconstructed versions · Effective dates unverified</summary>
                <p>{data.work.ordering_note}</p>
                {data.work.source_exception_count > 0 && (
                  <p>
                    {data.work.source_exception_count} reviewed source exceptions were not
                    applied. Read the source-text notes in the selected version.
                  </p>
                )}
              </details>
            )}
          </div>
        )}
      </QueryState>

      {/* Sticky Tab Navigation Bar */}
      <div className="sedlex-sticky-tabs-container">
        <nav className="sedlex-work-tabs" aria-label="Statutory Work Views">
          {tabs.map((t) => (
            <NavLink
              key={t.path}
              onMouseEnter={() => prefetchView(t.path)}
              onFocus={() => prefetchView(t.path)}
              to={t.path ? `/work/${workId}/${t.path}` : `/work/${workId}`}
              end={t.path === ''}
              className={({ isActive }) =>
                `sedlex-work-tab ${isActive ? 'is-active' : ''}`
              }
            >
              <t.icon aria-hidden="true" />
              <span>
                <b>{t.label}</b>
                <small>{t.detail}</small>
              </span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="sedlex-work-content">
        <Outlet />
      </div>
    </div>
  );
}
Component.displayName = 'WorkLayout';
